import { createSignal, createEffect, onMount, onCleanup, For, Show } from 'solid-js';
import { createStore } from 'solid-js/store';
import { useNavigate, useLocation } from '@solidjs/router';
import { currentUser, authChecked, authStore } from '../lib/auth-store.js';
import { api } from '../lib/api.js';
import { loadWorkspace, saveWorkspaceData, fitWorkspace } from '../lib/workspace.js';
import Connections from './Connections.jsx';
import Browser from './Browser.jsx';
import QuickOpenModal from '../components/QuickOpenModal.jsx';
import CommandPalette from '../components/CommandPalette.jsx';
import Users from './Users.jsx';
import Forms from './Forms.jsx';
import SavedImports from './SavedImports.jsx';
import Audit from './Audit.jsx';
import Tools from './Tools.jsx';
import Sessions from './Sessions.jsx';
import Settings from './Settings.jsx';
import BackupRestorePanel from './BackupRestorePanel.jsx';
import ResultHistoryPanel from './ResultHistoryPanel.jsx';
import FleetQueryPanel from './FleetQueryPanel.jsx';
import StructureSyncPanel from './StructureSyncPanel.jsx';
import DependencyPanel from './DependencyPanel.jsx';
import ScheduledJobsPanel from './ScheduledJobsPanel.jsx';
import FleetHealthPanel from './FleetHealthPanel.jsx';
import AccessAuditPanel from './AccessAuditPanel.jsx';
import AIActionsPanel from './AIActionsPanel.jsx';
import QueryComparePanel from './QueryComparePanel.jsx';
import ERDiagramPanel from './ERDiagramPanel.jsx';
import QueryBuilderPanel from './QueryBuilderPanel.jsx';
import SchemaDesignerPanel from './SchemaDesignerPanel.jsx';
import ReportBuilderPanel from './ReportBuilderPanel.jsx';
import ServerVariablesPanel from './ServerVariablesPanel.jsx';
import FormsDslPanel from './FormsDslPanel.jsx';
import ObjectViewer from '../components/ObjectViewer.jsx';
import FloatingPanel from '../components/FloatingPanel.jsx';
import TableComparePanel from './TableComparePanel.jsx';
import RefSyncPanel from './RefSyncPanel.jsx';
import AnomalyWatcherPanel from './AnomalyWatcherPanel.jsx';
import DualPane from '../components/DualPane.jsx';
import OpenMenu from '../components/OpenMenu.jsx';
import VersionHistory from '../components/VersionHistory.jsx';
import { VERSION } from '../lib/version.js';
import Toasts from '../components/Toasts.jsx';
import Modal from '../components/Modal.jsx';
import LockOverlay from '../components/LockOverlay.jsx';
import { installIdleLock, setOnForceLogout } from '../lib/lock.js';
import { refreshPinStatus } from '../lib/pin.js';
import { toast } from '../lib/toast.js';
import { focusMode, setFocusMode } from '../lib/focusmode.js';
import { theme, setTheme, THEMES } from '../lib/theme.js';
import { addScrap } from '../lib/scraps.js';
import { roleLabel } from '../lib/roles.js';

export default function Shell() {
  const navigate = useNavigate();
  const loc = useLocation();

  createEffect(() => {
    if (!authChecked()) authStore.refresh();
  });

  createEffect(() => {
    if (authChecked() && !currentUser()) {
      const next = encodeURIComponent(loc.pathname + loc.search);
      navigate(`/login?next=${next}`, { replace: true });
    }
  });

  // Tab list: each entry is { id, connectionId, connectionName }
  const [tabList, setTabList] = createSignal([]);

  // Per-tab navigation state — fine-grained reactive via store.
  // Shape: {
  //   [tabId]: {
  //     database: string|null,          // currently-expanded DB in the sidebar
  //     objects: {                       // the "Objects" inner tab — pinned at position 0
  //       open: bool,                    // closed by user via the × button
  //       database: string|null,
  //       category: 'tables'|'views'|'functions'|'events',
  //     },
  //     dataTabs: [{ id, database, table }, …],   // inserted RIGHT AFTER Objects
  //     activeInner: 'objects' | dataTab.id,
  //   }
  // }
  const [tabState, setTabState] = createStore({});

  const [activeTabId, setActiveTabId] = createSignal('home');

  // Connections (home) tab visibility: a user who can neither create a
  // connection nor see any shared one gets only the Forms tab. Admins always
  // may create; a 'user' needs the per-user flag; readonly never.
  const [connCount, setConnCount] = createSignal(null); // null = not loaded yet
  createEffect(() => {
    if (!currentUser()) return;
    api.get('/api/connections')
      .then((r) => setConnCount((r.connections || []).length))
      .catch(() => setConnCount(0));
  });
  const canCreate = () => {
    const u = currentUser();
    // A sub-admin is otherwise a regular 'user' for data, so it follows the same
    // can-create-connections gate.
    return u?.role === 'admin' || ((u?.role === 'user' || u?.role === 'subadmin') && !!u?.canCreateConnections);
  };
  // Show while the count is still loading (null) to avoid a flash; hide only
  // once we know there are zero connections and the user can't create. The
  // backup-operator role never gets the Connections home tab — its whole app is
  // the Backup/Restore tab (even though it may have connection grants for the
  // backup pickers).
  const isReadonly = () => currentUser()?.role === 'readonly';
  // Query/connection workspaces (Connections home, dual-pane, fleet query, the
  // object viewer): off for backup operators AND read-only accounts (the latter
  // are locked to Forms / Imports / Version History — see RO_TABS below).
  const hasWorkspaces = () => {
    const r = currentUser()?.role;
    return r !== 'backup' && r !== 'readonly';
  };
  const showConnTab = () =>
    hasWorkspaces() && (canCreate() || connCount() === null || connCount() > 0);

  // ── On-demand "system" sections ────────────────────────────────────────────
  // Everything except Connections is opened from the ☰ Open menu into a closable
  // top tab (rather than always-present). One registry drives the menu, the tab
  // buttons, the panels, and persistence. `allow(user)` is the single role gate;
  // `lazy` mirrors the old behaviour (mount the panel only while it's the active
  // tab, so it doesn't keep fetching/polling in the background).
  // A backup-role operator's entire app is the Backup/Restore tab — exclude it
  // from every other section. (It's orthogonal to the role hierarchy, so the
  // other gates don't naturally bar it.)
  const SYSTEM_TABS = [
    { id: 'forms',         label: 'Forms',    icon: '⊟', lazy: false, allow: (u) => u?.role !== 'backup' },
    // Imports is a CURATED write path (admin-authored, per-user granted), like Forms:
    // readonly may run only imports an admin granted them (list + run are grant-scoped;
    // create/edit/delete stay admin-only). This is intentionally separate from the
    // ad-hoc query write-block (readonly can't hand-write DML), matching the rule
    // "readonly can change data only via admin-supplied forms/imports."
    { id: 'saved-imports', label: 'Imports',  icon: '⬆', lazy: true,  allow: (u) => u?.role !== 'backup' },
    { id: 'sessions',      label: 'Sessions', icon: '⚇', lazy: true,  allow: (u) => u?.role !== 'backup' },
    { id: 'users',         label: 'Users',    icon: '👤', lazy: false, allow: (u) => u?.role === 'admin' || u?.role === 'subadmin' },
    { id: 'audit',         label: 'Audit',    icon: '🗐', lazy: true,  allow: (u) => u?.role === 'admin' || (u?.role !== 'admin' && u?.role !== 'backup' && !!u?.canViewOwnAudit) },
    { id: 'tools',         label: 'Tools',    icon: '🛠', lazy: true,  allow: (u) => u?.role === 'admin' || ((u?.role === 'user' || u?.role === 'subadmin') && !!u?.canCreateConnections) || !!u?.allowPortForward },
    // Backup / Restore tab — backup operators + admins. The backup role sees
    // ONLY this tab; admins get it too (and later the scheduled-job backups).
    { id: 'backup-restore', label: 'Backup / Restore', icon: '💾', lazy: true, allow: (u) => u?.role === 'admin' || u?.role === 'backup' || !!u?.canAccessBackupTab },
    { id: 'result-history', label: 'Result History', icon: '🕓', lazy: true, allow: (u) => u?.role !== 'backup' },
    // AI Actions — the write-approval queue for MCP propose_write. Admin only:
    // approving a proposal EXECUTES it, so it's the security gate for AI writes.
    { id: 'ai-actions',    label: 'AI Actions', icon: '🤖', lazy: true, allow: (u) => u?.role === 'admin' },
    { id: 'settings',      label: 'Settings', icon: '⚙', lazy: true,  allow: (u) => u?.role !== 'backup' },
  ];
  const systemMeta = (id) => SYSTEM_TABS.find((t) => t.id === id);
  // Read-only accounts see ONLY Forms + Imports here (plus Version History, which
  // isn't a system tab) — every other top-level section is hidden regardless of
  // per-user flags. This is the single enforcement point (feeds the tab bar, the
  // ☰ Open menu, and the openSystemTab guard), so a new section is hidden from
  // read-only by default.
  const RO_TABS = new Set(['forms', 'saved-imports']);
  const isSystemAllowed = (id) => {
    const t = systemMeta(id);
    if (!t) return false;
    if (isReadonly()) return RO_TABS.has(id);
    return t.allow(currentUser());
  };
  const allowedSystemTabs = () => SYSTEM_TABS.filter((t) => isSystemAllowed(t.id));

  const [openSystemTabs, setOpenSystemTabs] = createSignal([]); // ids, in open order

  // A sensible active tab when the current one goes away: Connections if visible,
  // else another open system tab, else the first connection tab, else home.
  const fallbackActiveTop = (excludeId) => {
    if (showConnTab()) return 'home';
    const sys = openSystemTabs().filter((x) => x !== excludeId);
    if (sys.length) return sys[0];
    const conn = tabList()[0];
    return conn ? conn.id : 'home';
  };

  const openSystemTab = (id, focus = true) => {
    if (!isSystemAllowed(id)) return;
    if (!openSystemTabs().includes(id)) setOpenSystemTabs((l) => [...l, id]);
    if (focus) setActiveTabId(id);
  };
  const closeSystemTab = (id) => {
    const wasActive = activeTabId() === id;
    setOpenSystemTabs((l) => l.filter((x) => x !== id));
    if (wasActive) setActiveTabId(fallbackActiveTop(id));
  };

  // Limited users (Connections tab hidden) always get a default tab so they're
  // never stranded on an empty workspace: the backup operator lands on
  // Backup/Restore (its only tab); everyone else on Forms.
  createEffect(() => {
    if (showConnTab()) return; // still loading or has Connections → skip
    const landing = currentUser()?.role === 'backup' ? 'backup-restore' : 'forms';
    if (!isSystemAllowed(landing)) return;
    if (!openSystemTabs().includes(landing)) setOpenSystemTabs((l) => l.includes(landing) ? l : [...l, landing]);
    if (activeTabId() === 'home') setActiveTabId(landing);
  });

  // ── Dual-pane tabs ─────────────────────────────────────────────────────────
  // Top-level tabs that stack two inner tabs (query/data) for side-by-side
  // comparison. A slot is a ref to an inner tab: { connTabId, innerId, kind,
  // resultsOnly }. The slot's live content is portaled in by the owning Browser
  // (Step 2); here we own the pane definitions + layout.
  const [dualPanes, setDualPanes] = createStore([]);
  let dualPaneSeq = 0;
  // Registry of slot body elements (the portal targets), keyed "paneId:slot".
  const [slotEls, setSlotEls] = createStore({});
  const registerSlotEl = (paneId, slot, el) => setSlotEls(`${paneId}:${slot}`, el || undefined);

  // ── Detached (floating) inner tabs ─────────────────────────────────────────
  // A floating panel hosts ONE inner tab's live DOM (relocated, never re-mounted —
  // same Approach-B trick as dual-pane slots, so the MySQL session/editor survive).
  // Shape: { id, connTabId, innerId, kind, x, y, w, h }. floatEls maps id → the
  // panel's body element (the relocation target), mirroring slotEls.
  const [floatingPanels, setFloatingPanels] = createStore([]);
  let floatSeq = 0;
  const [floatEls, setFloatEls] = createStore({});
  const registerFloatEl = (id, el) => setFloatEls(id, el || undefined);
  const patchFloatGeometry = (id, g) => {
    for (const k of ['x', 'y', 'w', 'h']) if (g[k] != null) setFloatingPanels((f) => f.id === id, k, g[k]);
  };

  const createDualPane = () => {
    const id = `dual-${++dualPaneSeq}`;
    setDualPanes((p) => [...p, { id, title: `Dual Pane ${dualPaneSeq}`, orientation: 'tb', ratio: 0.5, syncScroll: false, slot1: null, slot2: null }]);
    setActiveTabId(id);
    return id;
  };
  const patchDualPane = (id, key, value) => setDualPanes((d) => d.id === id, key, value);

  // ── Fleet query tabs ───────────────────────────────────────────────────────
  // Top-level tabs (siblings of dual-panes, NOT inner tabs of a connection)
  // that route each statement to a connection via the `[connection]` prefix.
  // They span the fleet, so they can't be owned by a single connection tab.
  // Persisted shape: { id, title, sql, defaultConnectionId, mode, presets }.
  // `mode` is 'free' (route via [connection] prefix) or 'preset' (run on a set
  // of checked connection+database targets); `presets` is the tab's working set
  // of { connectionId, database } pairs for preset mode. The session map,
  // results, and message log are all runtime-only (rebuilt each run).
  const [fleetTabs, setFleetTabs] = createStore([]);
  let fleetTabSeq = 0;
  // defaultConnectionId 0 ⇒ "none" (every statement must carry a [connection]
  // prefix). The Tools-card launcher passes 0; a future connection-toolbar
  // button can seed a real connection.
  const openFleetTab = (defaultConnectionId = 0) => {
    // No fleet query without workspaces (readonly/backup) — defence-in-depth
    // behind the hidden buttons/palette entries AND the restore path below.
    if (!hasWorkspaces()) return;
    const id = `fleet-${++fleetTabSeq}`;
    setFleetTabs((p) => [...p, {
      id, title: `Fleet Query ${fleetTabSeq}`, sql: '',
      defaultConnectionId: defaultConnectionId || 0,
      mode: 'free', presets: [],
    }]);
    setActiveTabId(id);
    return id;
  };
  const patchFleetTab = (id, key, value) => setFleetTabs((t) => t.id === id, key, value);
  const updateFleetTabSql = (id, sql) => {
    const t = fleetTabs.find((x) => x.id === id);
    if (!t || t.sql === sql) return;
    patchFleetTab(id, 'sql', sql);
  };
  const renameFleetTab = (id, title) => patchFleetTab(id, 'title', title);
  const setFleetTabDefaultConn = (id, connId) => patchFleetTab(id, 'defaultConnectionId', connId || 0);
  const setFleetTabMode = (id, mode) => patchFleetTab(id, 'mode', mode === 'preset' ? 'preset' : 'free');
  const setFleetTabPresets = (id, presets) => patchFleetTab(id, 'presets', Array.isArray(presets) ? presets : []);
  const closeFleetTab = (id) => {
    setFleetTabs((p) => p.filter((t) => t.id !== id));
    if (activeTabId() === id) setActiveTabId(fallbackActiveTop());
  };

  // Structure Synchronization tabs — top-level, like fleet tabs but NOT
  // persisted (a diff is a point-in-time snapshot; on reload the tab is gone).
  const [structureSyncTabs, setStructureSyncTabs] = createStore([]);
  let structureSyncSeq = 0;
  const openStructureSyncTab = () => {
    const id = `ssync-${++structureSyncSeq}`;
    setStructureSyncTabs((p) => [...p, { id, title: `Structure Sync ${structureSyncSeq}` }]);
    setActiveTabId(id);
    return id;
  };
  const closeStructureSyncTab = (id) => {
    setStructureSyncTabs((p) => p.filter((t) => t.id !== id));
    if (activeTabId() === id) setActiveTabId(fallbackActiveTop());
  };

  // Dependency Analyzer tabs — top-level, not persisted. `initial` pre-fills the
  // target when opened from a browser right-click (object or database).
  const [dependencyTabs, setDependencyTabs] = createStore([]);
  let dependencySeq = 0;
  const openDependencyTab = (initial) => {
    const id = `deps-${++dependencySeq}`;
    const title = initial?.name ? `Deps: ${initial.name}` : `Dependencies ${dependencySeq}`;
    setDependencyTabs((p) => [...p, { id, title, initial: initial || null }]);
    setActiveTabId(id);
    return id;
  };
  const closeDependencyTab = (id) => {
    setDependencyTabs((p) => p.filter((t) => t.id !== id));
    if (activeTabId() === id) setActiveTabId(fallbackActiveTop());
  };

  // Scheduled Jobs tab — top-level admin tool (not persisted; singleton-ish but
  // we allow re-open). Mirrors the structure-sync tab plumbing.
  const [scheduledJobsTabs, setScheduledJobsTabs] = createStore([]);
  let scheduledJobsSeq = 0;
  const openScheduledJobsTab = () => {
    // Re-focus an existing one rather than stacking duplicates.
    const existing = scheduledJobsTabs[0];
    if (existing) { setActiveTabId(existing.id); return existing.id; }
    const id = `jobs-${++scheduledJobsSeq}`;
    setScheduledJobsTabs((p) => [...p, { id, title: 'Scheduled Jobs' }]);
    setActiveTabId(id);
    return id;
  };
  const closeScheduledJobsTab = (id) => {
    setScheduledJobsTabs((p) => p.filter((t) => t.id !== id));
    if (activeTabId() === id) setActiveTabId(fallbackActiveTop());
  };

  // Fleet Health tab — top-level admin tool (not persisted — a probe is a
  // point-in-time snapshot). Singleton, mirrors the Scheduled Jobs plumbing.
  const [fleetHealthTabs, setFleetHealthTabs] = createStore([]);
  let fleetHealthSeq = 0;
  const openFleetHealthTab = () => {
    const existing = fleetHealthTabs[0];
    if (existing) { setActiveTabId(existing.id); return existing.id; }
    const id = `fhealth-${++fleetHealthSeq}`;
    setFleetHealthTabs((p) => [...p, { id, title: 'Fleet Health' }]);
    setActiveTabId(id);
    return id;
  };
  const closeFleetHealthTab = (id) => {
    setFleetHealthTabs((p) => p.filter((t) => t.id !== id));
    if (activeTabId() === id) setActiveTabId(fallbackActiveTop());
  };

  // Access / Security Auditor tab — top-level admin tool (not persisted — a probe
  // is a point-in-time snapshot). Singleton, mirrors the Fleet Health plumbing.
  const [accessAuditTabs, setAccessAuditTabs] = createStore([]);
  let accessAuditSeq = 0;
  const openAccessAuditTab = () => {
    const existing = accessAuditTabs[0];
    if (existing) { setActiveTabId(existing.id); return existing.id; }
    const id = `aaudit-${++accessAuditSeq}`;
    setAccessAuditTabs((p) => [...p, { id, title: 'Access Audit' }]);
    setActiveTabId(id);
    return id;
  };
  const closeAccessAuditTab = (id) => {
    setAccessAuditTabs((p) => p.filter((t) => t.id !== id));
    if (activeTabId() === id) setActiveTabId(fallbackActiveTop());
  };

  // Query Comparison tab — top-level admin tool (not persisted). Unlike the
  // others it is NOT a singleton: a user may want to compare several query pairs
  // at once, so each launch opens a fresh tab.
  const [queryCompareTabs, setQueryCompareTabs] = createStore([]);
  let queryCompareSeq = 0;
  const openQueryCompareTab = () => {
    const id = `qcompare-${++queryCompareSeq}`;
    setQueryCompareTabs((p) => [...p, { id, title: `Query Compare ${queryCompareSeq}` }]);
    setActiveTabId(id);
    return id;
  };
  const closeQueryCompareTab = (id) => {
    setQueryCompareTabs((p) => p.filter((t) => t.id !== id));
    if (activeTabId() === id) setActiveTabId(fallbackActiveTop());
  };

  // ER Diagram tab — top-level tool (not persisted).
  const [erDiagramTabs, setErDiagramTabs] = createStore([]);
  let erDiagramSeq = 0;
  const openERDiagramTab = () => {
    const id = `erdiagram-${++erDiagramSeq}`;
    setErDiagramTabs((p) => [...p, { id, title: `ER Diagram ${erDiagramSeq}` }]);
    setActiveTabId(id);
    return id;
  };
  const closeERDiagramTab = (id) => {
    setErDiagramTabs((p) => p.filter((t) => t.id !== id));
    if (activeTabId() === id) setActiveTabId(fallbackActiveTop());
  };

  // Visual Query Builder tab — top-level tool (not persisted). Non-singleton:
  // a user may build several queries side by side.
  const [queryBuilderTabs, setQueryBuilderTabs] = createStore([]);
  let queryBuilderSeq = 0;
  const openQueryBuilderTab = () => {
    const id = `qbuilder-${++queryBuilderSeq}`;
    setQueryBuilderTabs((p) => [...p, { id, title: `Query Builder ${queryBuilderSeq}` }]);
    setActiveTabId(id);
    return id;
  };
  const closeQueryBuilderTab = (id) => {
    setQueryBuilderTabs((p) => p.filter((t) => t.id !== id));
    if (activeTabId() === id) setActiveTabId(fallbackActiveTop());
  };

  // Schema Designer tab — top-level tool (not persisted). Non-singleton.
  const [schemaDesignerTabs, setSchemaDesignerTabs] = createStore([]);
  let schemaDesignerSeq = 0;
  const openSchemaDesignerTab = () => {
    const id = `schemadesign-${++schemaDesignerSeq}`;
    setSchemaDesignerTabs((p) => [...p, { id, title: `Schema Designer ${schemaDesignerSeq}` }]);
    setActiveTabId(id);
    return id;
  };
  const closeSchemaDesignerTab = (id) => {
    setSchemaDesignerTabs((p) => p.filter((t) => t.id !== id));
    if (activeTabId() === id) setActiveTabId(fallbackActiveTop());
  };

  // Server Variables & Status tab — top-level admin tool (not persisted).
  const [serverVarsTabs, setServerVarsTabs] = createStore([]);
  let serverVarsSeq = 0;
  const openServerVarsTab = () => {
    const id = `servervars-${++serverVarsSeq}`;
    setServerVarsTabs((p) => [...p, { id, title: `Server Variables ${serverVarsSeq}` }]);
    setActiveTabId(id);
    return id;
  };
  const closeServerVarsTab = (id) => {
    setServerVarsTabs((p) => p.filter((t) => t.id !== id));
    if (activeTabId() === id) setActiveTabId(fallbackActiveTop());
  };

  // Forms/Import DSL workbench tab — top-level admin tool (not persisted).
  const [formsDslTabs, setFormsDslTabs] = createStore([]);
  let formsDslSeq = 0;
  const openFormsDslTab = () => {
    const id = `formsdsl-${++formsDslSeq}`;
    setFormsDslTabs((p) => [...p, { id, title: `Forms DSL ${formsDslSeq}` }]);
    setActiveTabId(id);
    return id;
  };
  const closeFormsDslTab = (id) => {
    setFormsDslTabs((p) => p.filter((t) => t.id !== id));
    if (activeTabId() === id) setActiveTabId(fallbackActiveTop());
  };

  // Report Builder tab — top-level tool (not persisted). Singleton: it has its
  // own list/edit/view modes, so one tab is enough.
  const [reportBuilderTabs, setReportBuilderTabs] = createStore([]);
  let reportBuilderSeq = 0;
  const openReportBuilderTab = () => {
    const existing = reportBuilderTabs[0];
    if (existing) { setActiveTabId(existing.id); return existing.id; }
    const id = `reports-${++reportBuilderSeq}`;
    setReportBuilderTabs((p) => [...p, { id, title: 'Reports' }]);
    setActiveTabId(id);
    return id;
  };
  const closeReportBuilderTab = (id) => {
    setReportBuilderTabs((p) => p.filter((t) => t.id !== id));
    if (activeTabId() === id) setActiveTabId(fallbackActiveTop());
  };

  // Version History dialog (opened from the version label or the ☰ Open menu).
  const [versionOpen, setVersionOpen] = createSignal(false);

  // Open (or focus) a connection tab and seed a new query inner tab with `sql`.
  // Lets a standalone tool (the Query Builder) hand its generated SQL off to the
  // full query editor. `conn` is a { id, name, color? } from /api/connections.
  const openQueryInConnection = (conn, db, sql) => {
    if (!conn) return;
    let tab = tabList().find((t) => t.connectionId === conn.id);
    if (!tab) {
      openConnection(conn);
      tab = tabList().find((t) => t.connectionId === conn.id);
    }
    if (!tab) return;
    openQueryTabWith(tab.id, db, sql, false);
    setActiveTabId(tab.id);
  };

  // Table Comparison tab — top-level admin tool (not persisted). Not a singleton:
  // a user may compare several database pairs at once.
  const [tableCompareTabs, setTableCompareTabs] = createStore([]);
  let tableCompareSeq = 0;
  const openTableCompareTab = () => {
    const id = `tcompare-${++tableCompareSeq}`;
    setTableCompareTabs((p) => [...p, { id, title: `Table Compare ${tableCompareSeq}` }]);
    setActiveTabId(id);
    return id;
  };
  const closeTableCompareTab = (id) => {
    setTableCompareTabs((p) => p.filter((t) => t.id !== id));
    if (activeTabId() === id) setActiveTabId(fallbackActiveTop());
  };

  // Reference Data Sync tab — top-level admin tool (not persisted; a write flow
  // is a point-in-time action). Non-singleton: several sync setups at once.
  const [refSyncTabs, setRefSyncTabs] = createStore([]);
  let refSyncSeq = 0;
  const openRefSyncTab = () => {
    const id = `refsync-${++refSyncSeq}`;
    setRefSyncTabs((p) => [...p, { id, title: `Reference Sync ${refSyncSeq}` }]);
    setActiveTabId(id);
    return id;
  };
  const closeRefSyncTab = (id) => {
    setRefSyncTabs((p) => p.filter((t) => t.id !== id));
    if (activeTabId() === id) setActiveTabId(fallbackActiveTop());
  };

  // Data Anomaly Watcher tab — top-level admin tool. Singleton: it's a board over
  // all watchers (scheduled jobs of kind anomaly), so one is enough.
  const [anomalyTabs, setAnomalyTabs] = createStore([]);
  let anomalySeq = 0;
  const openAnomalyTab = () => {
    const existing = anomalyTabs[0];
    if (existing) { setActiveTabId(existing.id); return existing.id; }
    const id = `anomaly-${++anomalySeq}`;
    setAnomalyTabs((p) => [...p, { id, title: 'Anomaly Watcher' }]);
    setActiveTabId(id);
    return id;
  };
  const closeAnomalyTab = (id) => {
    setAnomalyTabs((p) => p.filter((t) => t.id !== id));
    if (activeTabId() === id) setActiveTabId(fallbackActiveTop());
  };

  // Which dual-pane slot (if any) a connection tab's inner tab is assigned to.
  // Returns { slotEl, resultsOnly } | null — reactive (reads dualPanes + slotEls).
  const assignmentFor = (connTabId, innerId) => {
    for (const pane of dualPanes) {
      for (const slot of ['slot1', 'slot2']) {
        const ref = pane[slot];
        if (ref && ref.connTabId === connTabId && ref.innerId === innerId) {
          return { slotEl: slotEls[`${pane.id}:${slot}`] || null, resultsOnly: !!ref.resultsOnly };
        }
      }
    }
    // A detached inner tab is "assigned" to its floating panel's body element, so
    // relocateWrap moves it there and the tab strip hides it — same as a slot.
    for (const fp of floatingPanels) {
      if (fp.connTabId === connTabId && fp.innerId === innerId) {
        return { slotEl: floatEls[fp.id] || null, resultsOnly: false, floating: true };
      }
    }
    return null;
  };

  // Display title for an inner tab, derived from its connection's live state.
  // Shared by dual-pane slot headers and floating-panel titles.
  const innerTabTitle = (connTabId, innerId, kind) => {
    const st = tabState[connTabId];
    if (kind === 'query') return (st?.queryTabs || []).find((q) => q.id === innerId)?.title || 'Query';
    if (kind === 'data') { const dt = (st?.dataTabs || []).find((d) => d.id === innerId); return dt ? dt.table : 'Data'; }
    if (kind === 'script') { const sc = (st?.scriptTabs || []).find((s) => s.id === innerId); return sc ? sc.name : 'Script'; }
    const te = (st?.tableEditTabs || []).find((x) => x.id === innerId);
    return te ? (te.create ? `New table · ${te.database}` : `${te.database} → ${te.table} (edit)`) : 'Table';
  };

  // Header info for a slot (title + kind), derived from the referenced inner tab.
  const slotInfo = (paneId, slot) => {
    const pane = dualPanes.find((p) => p.id === paneId);
    const ref = pane?.[slot];
    if (!ref) return null;
    return { title: innerTabTitle(ref.connTabId, ref.innerId, ref.kind), kind: ref.kind, resultsOnly: !!ref.resultsOnly };
  };

  // Drop any existing assignment of an inner tab (a tab lives in ≤ 1 slot).
  const clearAssignmentOf = (connTabId, innerId) => {
    for (const pane of dualPanes) {
      for (const slot of ['slot1', 'slot2']) {
        const ref = pane[slot];
        if (ref && ref.connTabId === connTabId && ref.innerId === innerId) {
          setDualPanes((d) => d.id === pane.id, slot, null);
        }
      }
    }
  };
  // Clear every slot pointing at a (closing) connection tab.
  const clearAssignmentsForConnTab = (connTabId) => {
    for (const pane of dualPanes) {
      for (const slot of ['slot1', 'slot2']) {
        if (pane[slot]?.connTabId === connTabId) setDualPanes((d) => d.id === pane.id, slot, null);
      }
    }
  };

  // Pick a sensible activeInner after a tab is sent away from its connection.
  const fallbackActiveInner = (connTabId, removedId) => {
    const st = tabState[connTabId];
    if (!st) return;
    if (st.activeInner !== removedId) return;
    const stillHome = (id) => !assignmentFor(connTabId, id);
    const q = (st.queryTabs || []).find((x) => x.id !== removedId && stillHome(x.id));
    const d = (st.dataTabs || []).find((x) => x.id !== removedId && stillHome(x.id));
    const s = (st.scriptTabs || []).find((x) => x.id !== removedId && stillHome(x.id));
    const te = (st.tableEditTabs || []).find((x) => x.id !== removedId && stillHome(x.id));
    setTabState(connTabId, 'activeInner', st.objects?.open ? 'objects' : (q?.id ?? d?.id ?? s?.id ?? te?.id ?? null));
  };

  const sendToDualPane = (connTabId, innerId, kind, target, slot) => {
    const paneId = target === 'new' ? createDualPane() : target;
    clearAssignmentOf(connTabId, innerId);
    setDualPanes((d) => d.id === paneId, slot, { connTabId, innerId, kind, resultsOnly: false });
    fallbackActiveInner(connTabId, innerId);
    setActiveTabId(paneId);
  };

  const ejectSlot = (paneId, slot) => {
    const pane = dualPanes.find((p) => p.id === paneId);
    const ref = pane?.[slot];
    setDualPanes((d) => d.id === paneId, slot, null);
    if (ref) setTabState(ref.connTabId, 'activeInner', ref.innerId); // show it again at home
  };

  const toggleSlotResultsOnly = (paneId, slot) => {
    const pane = dualPanes.find((p) => p.id === paneId);
    const ref = pane?.[slot];
    if (ref) setDualPanes((d) => d.id === paneId, slot, { ...ref, resultsOnly: !ref.resultsOnly });
  };

  const closeDualPane = (id) => {
    const pane = dualPanes.find((p) => p.id === id);
    if (pane) {
      for (const slot of ['slot1', 'slot2']) {
        const ref = pane[slot];
        if (ref) setTabState(ref.connTabId, 'activeInner', ref.innerId); // eject, don't destroy
      }
    }
    setDualPanes((p) => p.filter((d) => d.id !== id));
    if (activeTabId() === id) setActiveTabId(fallbackActiveTop());
  };

  // Detach an inner tab into a floating panel. Like sendToDualPane, it relocates
  // the live DOM (no re-mount): clear any existing slot, register the float, then
  // pick a new active inner since the tab leaves the strip.
  const detachToFloating = (connTabId, innerId, kind) => {
    if (floatingPanels.some((f) => f.connTabId === connTabId && f.innerId === innerId)) return; // already floating
    clearAssignmentOf(connTabId, innerId); // a tab lives in exactly one place
    const id = `float-${++floatSeq}`;
    const off = (floatingPanels.length % 6) * 26; // cascade so stacked detaches don't overlap exactly
    const w = 760, h = 520;
    const x = Math.max(16, Math.round((window.innerWidth || 1200) / 2 - w / 2) + off);
    setFloatingPanels((p) => [...p, { id, connTabId, innerId, kind, x, y: 84 + off, w, h }]);
    fallbackActiveInner(connTabId, innerId);
  };
  // Reattach a floating tab: drop the panel and show the tab at home again.
  const reattachFloating = (id) => {
    const fp = floatingPanels.find((f) => f.id === id);
    setFloatingPanels((p) => p.filter((f) => f.id !== id));
    if (fp) setTabState(fp.connTabId, 'activeInner', fp.innerId);
  };
  const clearFloatingForConnTab = (connTabId) => setFloatingPanels((p) => p.filter((f) => f.connTabId !== connTabId));

  // Workspace persistence: don't start saving until the saved snapshot has been
  // loaded + restored, otherwise the initial empty state would overwrite it.
  const [workspaceReady, setWorkspaceReady] = createSignal(false);

  // Called from Connections page when user clicks "Open"
  const openConnection = (c) => {
    const existing = tabList().find((t) => t.connectionId === c.id);
    if (existing) {
      setActiveTabId(existing.id);
      return;
    }
    const id = `conn-${c.id}`;
    // Auto-open the connection's default database (if set) the first time the
    // tab opens. This is the FRESH-open path only — a workspace restore builds
    // tabs from the snapshot without ever calling openConnection, so a user who
    // navigated away from the default DB keeps their restored state.
    const defDb = (c.defaultDatabase || '').trim();
    setTabList((prev) => [...prev, { id, connectionId: c.id, connectionName: c.name, connectionColor: c.color || '' }]);
    setTabState(id, {
      database: defDb || null,
      objects: defDb ? { open: true, database: defDb, category: 'tables' } : { open: false, database: null, category: 'tables' },
      mysqlUsersOpen: false,
      monitorOpen: false,
      dataTabs: [],
      queryTabs: [],          // each: { id, database, connectionId? (switched), sql, messages, sessionId, title }
      scriptTabs: [],         // each: { id, database, kind, name }
      tableEditTabs: [],      // each: { id, database, table } — full table editor in a tab
      activeInner: 'objects',
      // Per-database UI preferences (sort + filter for the Objects panel).
      // Shape: { [dbName]: { sort: 'alpha'|'usage', filter: string } }
      dbPrefs: {},
    });
    setActiveTabId(id);
  };

  // Persist per-database Objects-panel preferences (sort + quick filter).
  const setDbPref = (id, dbName, partial) => {
    const current = tabState[id]?.dbPrefs?.[dbName] ?? { sort: 'alpha', filter: '' };
    setTabState(id, 'dbPrefs', dbName, { ...current, ...partial });
  };

  const closeTab = (id, e) => {
    e.stopPropagation();
    clearAssignmentsForConnTab(id); // drop any dual-pane slots pointing here
    clearFloatingForConnTab(id); // and any detached floating panels
    const list = tabList();
    const idx = list.findIndex((t) => t.id === id);
    if (activeTabId() === id) {
      if (idx > 0) setActiveTabId(list[idx - 1].id);
      else if (list.length > 1) setActiveTabId(list[1].id);
      else setActiveTabId('home');
    }
    setTabList((prev) => prev.filter((t) => t.id !== id));
  };

  const selectDB = (id, db) => {
    setTabState(id, 'database', db);
  };

  // Show the Objects inner tab for (database, category). Opens/focuses it.
  const showObjects = (id, db, category) => {
    setTabState(id, 'objects', { open: true, database: db, category });
    setTabState(id, 'activeInner', 'objects');
  };

  // MySQL users inner tab — at most one per connection tab. Open / focus,
  // close. Admin-only on the backend; we let the sidebar's `Show when={admin}`
  // gate the entry-point UI.
  const showMySQLUsers = (id) => {
    setTabState(id, 'mysqlUsersOpen', true);
    setTabState(id, 'activeInner', 'mysql-users');
  };
  const closeMySQLUsers = (id) => {
    setTabState(id, 'mysqlUsersOpen', false);
    if (tabState[id]?.activeInner === 'mysql-users') {
      const fallback = tabState[id]?.objects?.open ? 'objects' :
                       (tabState[id]?.dataTabs?.[0]?.id ?? null);
      setTabState(id, 'activeInner', fallback);
    }
  };

  // Server / process monitor inner tab — at most one per connection tab.
  const showMonitor = (id) => {
    setTabState(id, 'monitorOpen', true);
    setTabState(id, 'activeInner', 'monitor');
  };
  const closeMonitor = (id) => {
    setTabState(id, 'monitorOpen', false);
    if (tabState[id]?.activeInner === 'monitor') {
      const fallback = tabState[id]?.objects?.open ? 'objects' :
                       (tabState[id]?.dataTabs?.[0]?.id ?? null);
      setTabState(id, 'activeInner', fallback);
    }
  };

  const closeObjectsTab = (id) => {
    setTabState(id, 'objects', 'open', false);
    // Fall back to the first data tab if any, otherwise no active inner.
    const dataTabs = tabState[id]?.dataTabs ?? [];
    setTabState(id, 'activeInner', dataTabs.length > 0 ? dataTabs[0].id : null);
  };

  // Open / focus a specific table-data inner tab. New tabs go RIGHT AFTER Objects.
  // Also bumps the per-user usage counter for "Most used" sorting.
  const openTableTab = (id, database, table, kind = 'table') => {
    const tab = tabList().find((t) => t.id === id);
    if (tab) {
      // Fire-and-forget — we don't block the UI on this.
      api.post(`/api/connections/${tab.connectionId}/databases/${encodeURIComponent(database)}/usage`,
        { kind, name: table }).catch(() => {});
    }
    const innerId = `tbl-${database}-${table}`;
    const existing = (tabState[id]?.dataTabs ?? []).find((t) => t.id === innerId);
    if (existing) {
      setTabState(id, 'activeInner', innerId);
      return;
    }
    setTabState(id, 'dataTabs', (prev) => [
      { id: innerId, database, table, filters: [] },
      ...(prev ?? []),
    ]);
    setTabState(id, 'activeInner', innerId);
  };

  // Update the filter list for a specific data tab.
  // Each filter past the first carries its own `connector` ('AND'/'OR').
  const setTableFilters = (id, innerId, filters) => {
    const list = tabState[id]?.dataTabs ?? [];
    const idx = list.findIndex((t) => t.id === innerId);
    if (idx < 0) return;
    setTabState(id, 'dataTabs', idx, 'filters', filters);
  };

  // Open (or focus) a data tab with filters pre-applied — used by FK navigation
  // to jump to a referenced parent row (refCol = value). Reusing an already-open
  // tab overwrites its filters, so repeated FK hops re-target the same parent tab.
  const openTableTabFiltered = (id, database, table, filters, kind = 'table') => {
    openTableTab(id, database, table, kind);
    setTableFilters(id, `tbl-${database}-${table}`, filters || []);
  };

  const closeTableTab = (id, innerId) => {
    clearAssignmentOf(id, innerId);
    const current = tabState[id]?.dataTabs ?? [];
    const idx = current.findIndex((t) => t.id === innerId);
    setTabState(id, 'dataTabs', current.filter((t) => t.id !== innerId));
    if (tabState[id]?.activeInner === innerId) {
      // Pick neighbour, prefer the one to the left (closer to Objects).
      const left = current[idx - 1];
      const right = current[idx + 1];
      const fallback = left?.id ?? right?.id
        ?? (tabState[id]?.objects?.open ? 'objects' : null);
      setTabState(id, 'activeInner', fallback);
    }
  };

  const setActiveInner = (id, innerId) => {
    setTabState(id, 'activeInner', innerId);
  };

  // Open a new query inner tab. Always opens a new one (so the user can have
  // many query scratchpads). Inserts right after Objects.
  let queryTabSeq = 0;
  const openQueryTab = (id) => {
    const state = tabState[id];
    if (!state?.database) return;  // Ctrl+Q is a no-op without a DB selected
    queryTabSeq += 1;
    const innerId = `qry-${Date.now()}-${queryTabSeq}`;
    setTabState(id, 'queryTabs', (prev) => [
      { id: innerId, database: state.database },
      ...(prev ?? []),
    ]);
    setTabState(id, 'activeInner', innerId);
  };

  // Open a new query tab SEEDED with SQL (used by the import wizard's "open in
  // query editor" mode). The seeded `sql` rides the same workspace-snapshot path
  // as a manually-typed query tab, so it survives reload.
  const openQueryTabWith = (id, database, sql, autoRun) => {
    const db = database || tabState[id]?.database;
    if (!db) return;
    queryTabSeq += 1;
    const innerId = `qry-${Date.now()}-${queryTabSeq}`;
    setTabState(id, 'queryTabs', (prev) => [
      // autoRun is a transient "run once on mount" flag — stripped from the saved
      // workspace (serializeWorkspace) so a reload never re-runs it.
      { id: innerId, database: db, sql: sql || '', autoRun: !!autoRun },
      ...(prev ?? []),
    ]);
    setTabState(id, 'activeInner', innerId);
  };

  // Open / focus a script-editor inner tab for a procedure/function/view.
  // Duplicates with the same (db, kind, name) are deduped; clicking again
  // just refocuses the existing tab.
  const openScriptTab = (id, database, kind, name) => {
    const innerId = `scr-${kind}-${database}-${name}`;
    const existing = (tabState[id]?.scriptTabs ?? []).find((t) => t.id === innerId);
    if (existing) {
      setTabState(id, 'activeInner', innerId);
      return;
    }
    setTabState(id, 'scriptTabs', (prev) => [
      { id: innerId, database, kind, name },
      ...(prev ?? []),
    ]);
    setTabState(id, 'activeInner', innerId);
  };

  // A blank "create routine" editor tab seeded with a DROP+CREATE skeleton (the
  // ＋ New procedure/function flow). Unique id so several can be open; on the
  // first successful save the host closes it and re-opens the real routine tab.
  let newScriptSeq = 0;
  const openNewScriptTab = (id, { database, kind, name, seedText }) => {
    newScriptSeq += 1;
    const innerId = `scr-new-${newScriptSeq}`;
    setTabState(id, 'scriptTabs', (prev) => [
      { id: innerId, database, kind, name, create: true, seedText },
      ...(prev ?? []),
    ]);
    setTabState(id, 'activeInner', innerId);
  };

  // Full table editor (columns/options/indexes/FKs/triggers) in an inner tab —
  // a dual-pane-sendable sibling of the Edit Table modal.
  const openTableEditTab = (id, database, table) => {
    const innerId = `tedit-${database}-${table}`;
    if ((tabState[id]?.tableEditTabs ?? []).some((t) => t.id === innerId)) {
      setTabState(id, 'activeInner', innerId);
      return;
    }
    setTabState(id, 'tableEditTabs', (prev) => [{ id: innerId, database, table }, ...(prev ?? [])]);
    setTabState(id, 'activeInner', innerId);
  };

  // Open an object (from the Dependency Analyzer) straight into a FLOATING editor:
  // make sure its connection tab is mounted, open the right inner tab, then detach
  // it. Code objects → ScriptEditor; tables → the full table editor. The active top
  // tab is preserved so the Dependency results stay visible behind the float.
  const openObjectFloating = ({ connectionId, connectionName, database, kind, name }) => {
    const connTabId = `conn-${connectionId}`;
    const prevActive = activeTabId();
    if (!tabList().some((t) => t.id === connTabId)) {
      openConnection({ id: connectionId, name: connectionName });
    }
    let innerId, innerKind;
    if (kind === 'table') {
      openTableEditTab(connTabId, database, name);
      innerId = `tedit-${database}-${name}`; innerKind = 'tableedit';
    } else {
      openScriptTab(connTabId, database, kind, name); // procedure | function | view | event
      innerId = `scr-${kind}-${database}-${name}`; innerKind = 'script';
    }
    detachToFloating(connTabId, innerId, innerKind);
    setActiveTabId(prevActive); // keep the Dependency results in front; the float overlays
  };

  // A blank "create table" editor in a tab (vs the modal). Each gets a unique id
  // so several can be open at once; closed on successful create.
  let newTableSeq = 0;
  const openNewTableEditTab = (id, database) => {
    newTableSeq += 1;
    const innerId = `tedit-new-${newTableSeq}`;
    setTabState(id, 'tableEditTabs', (prev) => [{ id: innerId, database, table: null, create: true }, ...(prev ?? [])]);
    setTabState(id, 'activeInner', innerId);
  };

  const closeTableEditTab = (id, innerId) => {
    clearAssignmentOf(id, innerId);
    const current = tabState[id]?.tableEditTabs ?? [];
    setTabState(id, 'tableEditTabs', current.filter((t) => t.id !== innerId));
    if (tabState[id]?.activeInner === innerId) {
      fallbackActiveInner(id, innerId);
    }
  };

  const closeScriptTab = (id, innerId) => {
    clearAssignmentOf(id, innerId);
    const current = tabState[id]?.scriptTabs ?? [];
    const idx = current.findIndex((t) => t.id === innerId);
    setTabState(id, 'scriptTabs', current.filter((t) => t.id !== innerId));
    if (tabState[id]?.activeInner === innerId) {
      const left = current[idx - 1];
      const right = current[idx + 1];
      const fallback = left?.id ?? right?.id
        ?? (tabState[id]?.objects?.open ? 'objects' : null)
        ?? (tabState[id]?.dataTabs?.[0]?.id ?? null);
      setTabState(id, 'activeInner', fallback);
    }
  };

  // QueryPanel → us, whenever its current DB changes (dropdown OR `USE foo`).
  // We patch the tab entry so InnerTabBar's label reflects it immediately.
  const updateQueryTabDb = (id, innerId, database) => {
    const list = tabState[id]?.queryTabs ?? [];
    const idx = list.findIndex((t) => t.id === innerId);
    if (idx < 0) return;
    if (list[idx].database === database) return;
    setTabState(id, 'queryTabs', idx, 'database', database);
  };

  // Persist a query tab's editor content (so reload restores the SQL).
  const updateQueryTabContent = (id, innerId, sql) => {
    const list = tabState[id]?.queryTabs ?? [];
    const idx = list.findIndex((t) => t.id === innerId);
    if (idx < 0 || list[idx].sql === sql) return;
    setTabState(id, 'queryTabs', idx, 'sql', sql);
  };

  // Persist a query tab's message log (so reload restores the run output).
  const updateQueryTabMessages = (id, innerId, messages) => {
    const list = tabState[id]?.queryTabs ?? [];
    const idx = list.findIndex((t) => t.id === innerId);
    if (idx < 0) return;
    setTabState(id, 'queryTabs', idx, 'messages', messages);
  };

  // Persist a query tab's current server session id, so after a reload the new
  // tab can ask the backend about the PREVIOUS session (still running? tx open?)
  // and label the restored in-flight statement honestly.
  const updateQueryTabSessionId = (id, innerId, sessionId) => {
    const list = tabState[id]?.queryTabs ?? [];
    const idx = list.findIndex((t) => t.id === innerId);
    if (idx < 0 || list[idx].sessionId === sessionId) return;
    setTabState(id, 'queryTabs', idx, 'sessionId', sessionId);
  };

  // Persist a query tab's switched connection (in-tab connection picker) so the
  // tab reopens on it after a reload. Absent ⇒ the Browser's connection.
  const updateQueryTabConnection = (id, innerId, connectionId) => {
    const list = tabState[id]?.queryTabs ?? [];
    const idx = list.findIndex((t) => t.id === innerId);
    if (idx < 0 || list[idx].connectionId === connectionId) return;
    setTabState(id, 'queryTabs', idx, 'connectionId', connectionId);
  };

  // Custom query tab title. Empty string reverts to the auto label ("Query #N").
  const renameQueryTab = (id, innerId, title) => {
    const list = tabState[id]?.queryTabs ?? [];
    const idx = list.findIndex((t) => t.id === innerId);
    if (idx < 0) return;
    setTabState(id, 'queryTabs', idx, 'title', title);
  };

  const closeQueryTab = (id, innerId) => {
    clearAssignmentOf(id, innerId);
    const current = tabState[id]?.queryTabs ?? [];
    const idx = current.findIndex((t) => t.id === innerId);
    // Save the closing tab's editor content as a "scrap" (7-day recovery bin) so
    // an accidental close doesn't lose work. Fire-and-forget; the server ignores
    // empty SQL. connectionId: the tab's switched conn, else the connection tab's.
    const closing = current[idx];
    if (closing && (closing.sql || '').trim()) {
      const connId = closing.connectionId || tabList().find((t) => t.id === id)?.connectionId || 0;
      addScrap({ title: closing.title || '', database: closing.database || '', connectionId: connId, sqlText: closing.sql })
        .catch(() => {});
    }
    setTabState(id, 'queryTabs', current.filter((t) => t.id !== innerId));
    if (tabState[id]?.activeInner === innerId) {
      const left = current[idx - 1];
      const right = current[idx + 1];
      const fallback = left?.id ?? right?.id
        ?? (tabState[id]?.objects?.open ? 'objects' : null)
        ?? (tabState[id]?.dataTabs?.[0]?.id ?? null);
      setTabState(id, 'activeInner', fallback);
    }
  };

  // Outer (workspace) tabs in left-to-right render order — the list Ctrl+1..9
  // indexes into. Mirrors the tab-bar render (home → system → connections →
  // dual-panes).
  const orderedTopTabs = () => [
    ...(showConnTab() ? ['home'] : []),
    ...openSystemTabs(),
    ...tabList().map((t) => t.id),
    ...dualPanes.map((p) => p.id),
    ...fleetTabs.map((t) => t.id),
    ...structureSyncTabs.map((t) => t.id),
    ...dependencyTabs.map((t) => t.id),
    ...scheduledJobsTabs.map((t) => t.id),
    ...fleetHealthTabs.map((t) => t.id),
    ...accessAuditTabs.map((t) => t.id),
    ...queryCompareTabs.map((t) => t.id),
    ...erDiagramTabs.map((t) => t.id),
    ...queryBuilderTabs.map((t) => t.id),
    ...schemaDesignerTabs.map((t) => t.id),
    ...reportBuilderTabs.map((t) => t.id),
    ...serverVarsTabs.map((t) => t.id),
    ...formsDslTabs.map((t) => t.id),
    ...tableCompareTabs.map((t) => t.id),
    ...refSyncTabs.map((t) => t.id),
    ...anomalyTabs.map((t) => t.id),
  ];
  // A connection tab's inner tabs in render order (mirrors Browser's InnerTabBar)
  // — the list Ctrl+Tab / Ctrl+Shift+Tab cycles through.
  const orderedInnerTabs = (connId) => {
    const s = tabState[connId];
    if (!s) return [];
    return [
      ...(s.objects?.open ? ['objects'] : []),
      ...(s.mysqlUsersOpen ? ['mysql-users'] : []),
      ...(s.monitorOpen ? ['monitor'] : []),
      ...(s.queryTabs ?? []).map((t) => t.id),
      ...(s.scriptTabs ?? []).map((t) => t.id),
      ...(s.tableEditTabs ?? []).map((t) => t.id),
      ...(s.dataTabs ?? []).map((t) => t.id),
    ];
  };

  // Global keyboard shortcuts:
  //   Ctrl+Q              new query tab in the active connection (needs a DB).
  //   Ctrl+1 … Ctrl+9     jump to the Nth outer (workspace) tab.
  //   Ctrl+Tab / +Shift   cycle inner tabs of the active connection forward/back.
  // NOTE: Ctrl+Tab and Ctrl+<digit> are browser-reserved (tab switching); in a
  // plain browser tab they may be swallowed before reaching the page. They work
  // in app/standalone windows. preventDefault is best-effort.
  // ── Search Everywhere (Ctrl+P) ──────────────────────────────────────────────
  // Quick-open any table/view/routine across the active connection's schema.
  const [quickOpen, setQuickOpen] = createSignal(null); // { connectionId, database, tabId }
  const onQuickOpen = (entry) => {
    const qo = quickOpen();
    if (!qo) return;
    setActiveTabId(qo.tabId);
    if (entry.kind === 'table' || entry.kind === 'view') {
      openTableTab(qo.tabId, entry.database, entry.name, entry.kind);
    } else {
      // Routine — open its source via SHOW CREATE in a query tab (auto-run).
      const kw = entry.kind === 'function' ? 'FUNCTION' : 'PROCEDURE';
      openQueryTabWith(qo.tabId, entry.database, `SHOW CREATE ${kw} \`${entry.database}\`.\`${entry.name}\`;`, true);
    }
  };

  // ── Command palette (Ctrl+K) ────────────────────────────────────────────────
  const [cmdOpen, setCmdOpen] = createSignal(false);
  const [objViewerOpen, setObjViewerOpen] = createSignal(false); // Object Viewer palette (Ctrl+F12)
  // The actions available right now, given the active tab + the user's role.
  const buildCommands = () => {
    const cmds = [];
    const aid = activeTabId();
    const connTab = tabList().find((t) => t.id === aid);
    const u = currentUser();
    if (connTab) {
      cmds.push({ label: 'Search Everywhere', group: 'Go', shortcut: 'Ctrl+P',
        run: () => setQuickOpen({ connectionId: connTab.connectionId, database: tabState[aid]?.database || '', tabId: aid }) });
      if (tabState[aid]?.database)
        cmds.push({ label: 'New query tab', group: 'Query', shortcut: 'Ctrl+Q', run: () => openQueryTab(aid) });
    }
    if (showConnTab())
      cmds.push({ label: 'Go to Connections', group: 'Go', run: () => setActiveTabId('home') });
    if (hasWorkspaces()) {
      cmds.push({ label: 'New dual pane', group: 'Workspace', run: () => createDualPane() });
      cmds.push({ label: 'New fleet query', group: 'Workspace', run: () => openFleetTab() });
    }
    for (const t of allowedSystemTabs())
      cmds.push({ label: `Open ${t.label}`, group: 'Open', run: () => openSystemTab(t.id) });
    cmds.push({ label: focusMode() ? 'Exit focus mode' : 'Enter focus mode (distraction-free)', group: 'View', run: () => setFocusMode(!focusMode()) });
    cmds.push({ label: 'Sign out…', group: 'Account', run: () => setSignoutStep('menu') });
    return cmds;
  };

  const onGlobalKey = (e) => {
    // Escape leaves distraction-free mode. Gated on !defaultPrevented so a prior
    // consumer of this Escape — CodeMirror closing its autocomplete popup, a modal
    // dismissing — wins; only a "bare" Escape exits focus. (This handler bubbles
    // after the editor, so defaultPrevented already reflects CM's handling.)
    if (e.key === 'Escape' && focusMode() && !e.defaultPrevented) { setFocusMode(false); return; }
    const ctrl = e.ctrlKey || e.metaKey;
    if (!ctrl) return;

    // Ctrl/Cmd+F12 — toggle the floating Object Viewer palette (a connection
    // schema browser; not for readonly/backup, who have no connection surface).
    if (e.key === 'F12' && !e.shiftKey && !e.altKey) {
      e.preventDefault();
      if (hasWorkspaces()) setObjViewerOpen((v) => !v);
      return;
    }

    // Ctrl/Cmd+K — command palette (action launcher).
    if ((e.key === 'k' || e.key === 'K') && !e.shiftKey && !e.altKey) {
      e.preventDefault();
      setCmdOpen(true);
      return;
    }

    // Ctrl/Cmd+P — Search Everywhere over the active connection's schema.
    if ((e.key === 'p' || e.key === 'P') && !e.shiftKey && !e.altKey) {
      const aid = activeTabId();
      const tab = tabList().find((t) => t.id === aid);
      if (!tab) return; // only on a connection tab (needs that connection's schema cache)
      e.preventDefault();
      setQuickOpen({ connectionId: tab.connectionId, database: tabState[aid]?.database || '', tabId: aid });
      return;
    }

    if (e.key === 'q' || e.key === 'Q') {
      const aid = activeTabId();
      if (!tabList().some((t) => t.id === aid)) return;
      if (!tabState[aid]?.database) return;
      e.preventDefault();
      openQueryTab(aid);
      return;
    }

    if (e.key >= '1' && e.key <= '9') {
      const target = orderedTopTabs()[Number(e.key) - 1];
      if (target) { e.preventDefault(); setActiveTabId(target); }
      return;
    }

    if (e.key === 'Tab') {
      const aid = activeTabId();
      if (!tabList().some((t) => t.id === aid)) return; // only connection tabs have inner tabs
      const inner = orderedInnerTabs(aid);
      if (inner.length < 2) return;
      let idx = inner.indexOf(tabState[aid]?.activeInner);
      if (idx === -1) idx = 0;
      const step = e.shiftKey ? -1 : 1;
      e.preventDefault();
      setActiveInner(aid, inner[(idx + step + inner.length) % inner.length]);
    }
  };
  onMount(() => document.addEventListener('keydown', onGlobalKey));

  // Idle lock + auto-logout (Settings → Security). The lock overlay shows after
  // idleLockSeconds (but only when a PIN is set — see pin.js's pinEnabled);
  // forceLogoutSeconds independently triggers a real sign-out for THIS device.
  // We boot-fetch the PIN status so the lock decision is made on real data,
  // not the default-false signal (which would suppress the lock until the
  // next setPin/clearPin call).
  refreshPinStatus();
  setOnForceLogout(() => doLogout('device'));
  installIdleLock();
  onCleanup(() => document.removeEventListener('keydown', onGlobalKey));

  // Sign-out confirmation dialog. `signoutStep`:
  //   null            — closed
  //   'menu'          — choose scope (this device / all my devices / everyone)
  //   'confirm-all'   — second confirm for the admin "sign out everyone"
  const [signoutStep, setSignoutStep] = createSignal(null);
  const [signingOut, setSigningOut] = createSignal(false);
  const isAdmin = () => currentUser()?.role === 'admin';
  const doLogout = async (scope) => {
    setSigningOut(true);
    try {
      const count = await authStore.logout(scope);
      if (scope === 'everyone') toast.success(`Signed out all users (${count} session${count === 1 ? '' : 's'}).`);
      else if (scope === 'mine') toast.success(`Signed out of all your devices (${count} session${count === 1 ? '' : 's'}).`);
      navigate('/login', { replace: true });
    } finally {
      setSigningOut(false);
      setSignoutStep(null);
    }
  };

  // ── Workspace persistence ──────────────────────────────────────────────────
  // Serialize the whole workspace (open connection tabs + their inner tabs).
  // Reading the store fields here — plus JSON.stringify's deep walk in the save
  // effect below — makes the effect track every nested change.
  const serializeWorkspace = () => {
    const list = tabList();
    const states = {};
    for (const t of list) {
      const st = tabState[t.id];
      if (!st) continue;
      states[t.id] = {
        database: st.database,
        objects: st.objects,
        mysqlUsersOpen: st.mysqlUsersOpen,
        monitorOpen: st.monitorOpen,
        dataTabs: st.dataTabs,
        // Strip the transient autoRun flag so a restored tab never re-runs.
        queryTabs: (st.queryTabs || []).map(({ autoRun, ...q }) => q),
        scriptTabs: st.scriptTabs,
        tableEditTabs: st.tableEditTabs,
        activeInner: st.activeInner,
      };
    }
    return {
      v: 1,
      activeTabId: activeTabId(),
      systemTabs: openSystemTabs(),
      tabs: list.map((t) => ({ connectionId: t.connectionId, connectionName: t.connectionName })),
      states,
      dualPanes: dualPanes.map((p) => ({
        id: p.id, title: p.title, orientation: p.orientation, ratio: p.ratio,
        syncScroll: p.syncScroll, slot1: p.slot1, slot2: p.slot2,
      })),
      floatingPanels: floatingPanels.map((f) => ({
        id: f.id, connTabId: f.connTabId, innerId: f.innerId, kind: f.kind,
        x: f.x, y: f.y, w: f.w, h: f.h,
      })),
      fleetTabs: fleetTabs.map((t) => ({
        id: t.id, title: t.title, sql: t.sql, defaultConnectionId: t.defaultConnectionId,
        mode: t.mode || 'free', presets: t.presets || [],
      })),
    };
  };

  // Restore a saved workspace, skipping connections that no longer exist.
  const restoreWorkspace = async () => {
    let snap;
    try {
      snap = await loadWorkspace();
    } catch (e) {
      // The saved workspace couldn't be READ (network / server error) — not the
      // same as "none saved". Saving stays off for this session so the empty
      // startup state never overwrites the stored copy; a reload retries.
      if (e?.message !== 'unauthorized') {
        toast.error(`Couldn't load your saved workspace (${e?.message || e}). Tab changes won't be saved this session — reload to retry.`);
      }
      return;
    }
    try {
      if (!snap) return;

      // On-demand system sections — restore first; they can exist with no
      // connection tabs at all (e.g. a Forms-only user). Drop any the current
      // role isn't allowed to see.
      const sysOpen = (snap.systemTabs || []).filter((id) => isSystemAllowed(id));
      setOpenSystemTabs(sysOpen);

      // Read-only / backup accounts have no connection, dual-pane, or fleet
      // workspaces — never rebuild them from a stale snapshot (e.g. an account
      // demoted to read-only). Only the allowed system tabs above are restored.
      let conns = [];
      if (hasWorkspaces()) {
        try { conns = (await api.get('/api/connections')).connections || []; } catch {}
      }
      const byId = new Map(conns.map((c) => [c.id, c]));
      const valid = hasWorkspaces()
        ? (snap.tabs || []).filter((t) => !byId.size || byId.has(t.connectionId))
        : [];
      const newList = valid.map((t) => ({
        id: `conn-${t.connectionId}`,
        connectionId: t.connectionId,
        connectionName: byId.get(t.connectionId)?.name || t.connectionName,
        connectionColor: byId.get(t.connectionId)?.color || '', // re-derived fresh, never persisted
      }));
      setTabList(newList);
      for (const t of newList) {
        const st = snap.states?.[t.id] || {};
        setTabState(t.id, {
          database: st.database ?? null,
          objects: st.objects ?? { open: false, database: null, category: 'tables' },
          mysqlUsersOpen: st.mysqlUsersOpen ?? false,
          monitorOpen: st.monitorOpen ?? false,
          dataTabs: st.dataTabs ?? [],
          queryTabs: st.queryTabs ?? [],
          scriptTabs: st.scriptTabs ?? [],
          tableEditTabs: st.tableEditTabs ?? [],
          activeInner: st.activeInner ?? 'objects',
          dbPrefs: {},
        });
      }
      // Restore dual-pane tabs (only meaningful with connection tabs to point at),
      // dropping slot refs whose connection/tab is gone.
      let restoredPanes = [];
      if (newList.length) {
        const validTabIds = new Set(newList.map((t) => t.id));
        const validRef = (ref) => {
          if (!ref || !validTabIds.has(ref.connTabId)) return null;
          const st = snap.states?.[ref.connTabId] || {};
          const list = ref.kind === 'query' ? (st.queryTabs || [])
            : ref.kind === 'data' ? (st.dataTabs || [])
            : ref.kind === 'script' ? (st.scriptTabs || [])
            : (st.tableEditTabs || []);
          return list.some((x) => x.id === ref.innerId) ? { ...ref } : null;
        };
        restoredPanes = (snap.dualPanes || []).map((p) => ({
          id: p.id, title: p.title || 'Dual Pane',
          orientation: p.orientation || 'tb', ratio: p.ratio ?? 0.5,
          syncScroll: !!p.syncScroll,
          slot1: validRef(p.slot1), slot2: validRef(p.slot2),
        }));
        setDualPanes(restoredPanes);
        dualPaneSeq = restoredPanes.reduce((mx, p) => {
          const n = parseInt(String(p.id).replace('dual-', ''), 10);
          return Number.isFinite(n) && n > mx ? n : mx;
        }, 0);

        // Detached floating panels — validRef keeps the geometry (it spreads the
        // whole ref) and drops any whose connection/inner tab is gone.
        const restoredFloats = (snap.floatingPanels || []).map(validRef).filter(Boolean);
        setFloatingPanels(restoredFloats);
        floatSeq = restoredFloats.reduce((mx, f) => {
          const n = parseInt(String(f.id).replace('float-', ''), 10);
          return Number.isFinite(n) && n > mx ? n : mx;
        }, 0);
      }

      // Fleet tabs are cross-connection — restore them even with zero connection
      // tabs (e.g. a user who only does fleet runs). The session map/results are
      // runtime-only, so only sql/title/defaultConnectionId come back.
      const restoredFleet = hasWorkspaces() ? (snap.fleetTabs || []).map((t) => ({
        id: t.id, title: t.title || 'Fleet Query', sql: t.sql || '',
        defaultConnectionId: t.defaultConnectionId || 0,
        mode: t.mode === 'preset' ? 'preset' : 'free',
        presets: Array.isArray(t.presets) ? t.presets : [],
      })) : [];
      setFleetTabs(restoredFleet);
      fleetTabSeq = restoredFleet.reduce((mx, t) => {
        const n = parseInt(String(t.id).replace('fleet-', ''), 10);
        return Number.isFinite(n) && n > mx ? n : mx;
      }, 0);

      const validTabs = new Set([
        'home', ...sysOpen,
        ...newList.map((t) => t.id), ...restoredPanes.map((p) => p.id),
        ...restoredFleet.map((t) => t.id),
      ]);
      setActiveTabId(
        validTabs.has(snap.activeTabId) ? snap.activeTabId
          : (newList[0]?.id ?? sysOpen[0] ?? 'home'));
    } finally {
      setWorkspaceReady(true); // enable saving from here on
    }
  };

  // Restore once per logged-in user; reset (without saving) on logout / switch.
  let lastUserId = null;
  createEffect(() => {
    if (!authChecked()) return;
    const uid = currentUser()?.id ?? null;
    if (uid === lastUserId) return;
    lastUserId = uid;
    setWorkspaceReady(false);
    setTabList([]);
    setDualPanes([]);
    setFleetTabs([]);
    setOpenSystemTabs([]);
    setActiveTabId('home');
    if (uid != null) restoreWorkspace();
  });

  // Debounced save whenever the workspace changes (after the initial restore).
  let saveTimer = null;
  createEffect(() => {
    const snap = serializeWorkspace();
    const json = JSON.stringify(snap); // deep walk → deep dependency tracking
    if (!workspaceReady()) return;
    // Persist when there's anything worth restoring — connection tabs, open
    // system sections, dual panes, or fleet tabs. Otherwise clear the snapshot.
    const hasContent = snap.tabs.length || (snap.systemTabs?.length) || snap.dualPanes.length || snap.fleetTabs.length;
    if (saveTimer) clearTimeout(saveTimer);
    // fitWorkspace trims run-message history if the snapshot is over the
    // server's size cap (null = still too big → saveWorkspaceData warns).
    saveTimer = setTimeout(() => saveWorkspaceData(hasContent ? fitWorkspace(snap, json) : ''), 800);
  });
  onCleanup(() => { if (saveTimer) clearTimeout(saveTimer); });

  return (
    <Show
      when={currentUser()}
      fallback={
        <div class="login-wrap">
          <div class="muted">Checking session…</div>
        </div>
      }
    >
      <div class="app" classList={{ 'focus-mode': focusMode() }}>
        {/* ── Top bar ── */}
        <header class="topbar">
          <div class="brand">
            <h1 class="logo">
              <span class="logo-mark" /> Ninocat
            </h1>
            <button class="app-version" title="What’s new — version history"
                    onClick={() => setVersionOpen(true)}>v{VERSION}</button>
          </div>
          <div class="topbar-spacer" />
          <div class="topbar-user">
            <span>{currentUser().username}</span>
            <span class={`role-pill role-${currentUser().role}`}>
              {roleLabel(currentUser().role)}
            </span>
            <select class="input theme-select" title="Theme"
                    value={theme()} onChange={(e) => setTheme(e.target.value)}>
              <optgroup label="Dark">
                <For each={THEMES.filter((t) => t.base === 'dark')}>
                  {(t) => <option value={t.id}>{t.label}</option>}
                </For>
              </optgroup>
              <optgroup label="Light">
                <For each={THEMES.filter((t) => t.base === 'light')}>
                  {(t) => <option value={t.id}>{t.label}</option>}
                </For>
              </optgroup>
            </select>
            <button class="btn btn-ghost" onClick={() => setSignoutStep('menu')}>
              Sign out
            </button>
          </div>
          {/* Reserves the OS window-controls width in PWA Window-Controls-Overlay
              mode, so Sign out / theme picker never sit under the min/max/close
              buttons. Zero-width (display:none) everywhere else. */}
          <div class="wco-spacer" />
        </header>

        {/* ── Tab bar ── */}
        <div class="tab-bar">
          {/* Pinned group — the ☰ Open launcher and the Connections home tab stay
              fixed at the left; every other tab scrolls horizontally under them. */}
          <div class="tab-bar-pinned">
          {/* Launcher — opens the on-demand sections (role-filtered) as tabs */}
          <OpenMenu
            items={[
              ...allowedSystemTabs().map((t) => ({
                id: t.id, label: t.label, icon: t.icon, open: openSystemTabs().includes(t.id),
              })),
              { id: 'version-history', label: 'Version history', icon: 'ⓘ' },
            ]}
            onPick={(id) => { if (id === 'version-history') setVersionOpen(true); else openSystemTab(id); }}
          />

          {/* Home tab — hidden for a user with no connections + no create right */}
          <Show when={showConnTab()}>
            <button
              class="tab"
              classList={{ active: activeTabId() === 'home' }}
              onClick={() => setActiveTabId('home')}
            >
              <span class="tab-home-icon">⊞</span>
              Connections
            </button>
          </Show>
          </div>

          {/* On-demand system sections — opened from the ☰ menu, closable */}
          <For each={openSystemTabs()}>
            {(id) => {
              const meta = systemMeta(id);
              return (
                <button
                  class="tab"
                  classList={{ active: activeTabId() === id }}
                  onClick={() => setActiveTabId(id)}
                >
                  <span class="tab-home-icon">{meta?.icon}</span>
                  <span class="tab-label">{meta?.label}</span>
                  <span class="tab-close" onClick={(e) => { e.stopPropagation(); closeSystemTab(id); }}>×</span>
                </button>
              );
            }}
          </For>

          <For each={tabList()}>
            {(tab) => (
              <button
                class="tab"
                classList={{ active: activeTabId() === tab.id, 'conn-tinted': !!tab.connectionColor }}
                style={tab.connectionColor ? `--conn-color: ${tab.connectionColor};` : ''}
                onClick={() => setActiveTabId(tab.id)}
                title={tab.connectionColor ? `${tab.connectionName} — color-tagged` : tab.connectionName}
              >
                <span class="conn-dot-sm" style={tab.connectionColor ? `background: ${tab.connectionColor};` : ''} />
                <span class="tab-label">{tab.connectionName}</span>
                <span class="tab-close" onClick={(e) => closeTab(tab.id, e)}>
                  ×
                </span>
              </button>
            )}
          </For>

          {/* Dual-pane tabs */}
          <For each={dualPanes}>
            {(pane) => (
              <button
                class="tab"
                classList={{ active: activeTabId() === pane.id }}
                onClick={() => setActiveTabId(pane.id)}
              >
                <span class="tab-home-icon">▥</span>
                <span class="tab-label">{pane.title}</span>
                <span class="tab-close" onClick={(e) => { e.stopPropagation(); closeDualPane(pane.id); }}>×</span>
              </button>
            )}
          </For>

          {/* Fleet query tabs — top-level, cross-connection */}
          <For each={fleetTabs}>
            {(t) => (
              <button
                class="tab"
                classList={{ active: activeTabId() === t.id }}
                onClick={() => setActiveTabId(t.id)}
              >
                <span class="tab-home-icon">⚡</span>
                <span class="tab-label">{t.title}</span>
                <span class="tab-close" onClick={(e) => { e.stopPropagation(); closeFleetTab(t.id); }}>×</span>
              </button>
            )}
          </For>

          {/* Structure Sync tabs — top-level */}
          <For each={structureSyncTabs}>
            {(t) => (
              <button
                class="tab"
                classList={{ active: activeTabId() === t.id }}
                onClick={() => setActiveTabId(t.id)}
              >
                <span class="tab-home-icon">🔀</span>
                <span class="tab-label">{t.title}</span>
                <span class="tab-close" onClick={(e) => { e.stopPropagation(); closeStructureSyncTab(t.id); }}>×</span>
              </button>
            )}
          </For>

          {/* Dependency Analyzer tabs — top-level */}
          <For each={dependencyTabs}>
            {(t) => (
              <button
                class="tab"
                classList={{ active: activeTabId() === t.id }}
                onClick={() => setActiveTabId(t.id)}
              >
                <span class="tab-home-icon">🧭</span>
                <span class="tab-label">{t.title}</span>
                <span class="tab-close" onClick={(e) => { e.stopPropagation(); closeDependencyTab(t.id); }}>×</span>
              </button>
            )}
          </For>

          {/* Scheduled Jobs tab — top-level */}
          <For each={scheduledJobsTabs}>
            {(t) => (
              <button
                class="tab"
                classList={{ active: activeTabId() === t.id }}
                onClick={() => setActiveTabId(t.id)}
              >
                <span class="tab-home-icon">⏰</span>
                <span class="tab-label">{t.title}</span>
                <span class="tab-close" onClick={(e) => { e.stopPropagation(); closeScheduledJobsTab(t.id); }}>×</span>
              </button>
            )}
          </For>

          {/* Fleet Health tab — top-level */}
          <For each={fleetHealthTabs}>
            {(t) => (
              <button
                class="tab"
                classList={{ active: activeTabId() === t.id }}
                onClick={() => setActiveTabId(t.id)}
              >
                <span class="tab-home-icon">📊</span>
                <span class="tab-label">{t.title}</span>
                <span class="tab-close" onClick={(e) => { e.stopPropagation(); closeFleetHealthTab(t.id); }}>×</span>
              </button>
            )}
          </For>

          {/* Access Audit tab — top-level */}
          <For each={accessAuditTabs}>
            {(t) => (
              <button
                class="tab"
                classList={{ active: activeTabId() === t.id }}
                onClick={() => setActiveTabId(t.id)}
              >
                <span class="tab-home-icon">🛡️</span>
                <span class="tab-label">{t.title}</span>
                <span class="tab-close" onClick={(e) => { e.stopPropagation(); closeAccessAuditTab(t.id); }}>×</span>
              </button>
            )}
          </For>

          {/* Query Comparison tab — top-level */}
          <For each={queryCompareTabs}>
            {(t) => (
              <button
                class="tab"
                classList={{ active: activeTabId() === t.id }}
                onClick={() => setActiveTabId(t.id)}
              >
                <span class="tab-home-icon">⚖️</span>
                <span class="tab-label">{t.title}</span>
                <span class="tab-close" onClick={(e) => { e.stopPropagation(); closeQueryCompareTab(t.id); }}>×</span>
              </button>
            )}
          </For>

          {/* ER Diagram tab — top-level */}
          <For each={erDiagramTabs}>
            {(t) => (
              <button
                class="tab"
                classList={{ active: activeTabId() === t.id }}
                onClick={() => setActiveTabId(t.id)}
              >
                <span class="tab-home-icon">🗺️</span>
                <span class="tab-label">{t.title}</span>
                <span class="tab-close" onClick={(e) => { e.stopPropagation(); closeERDiagramTab(t.id); }}>×</span>
              </button>
            )}
          </For>

          {/* Visual Query Builder tab — top-level */}
          <For each={queryBuilderTabs}>
            {(t) => (
              <button
                class="tab"
                classList={{ active: activeTabId() === t.id }}
                onClick={() => setActiveTabId(t.id)}
              >
                <span class="tab-home-icon">🧩</span>
                <span class="tab-label">{t.title}</span>
                <span class="tab-close" onClick={(e) => { e.stopPropagation(); closeQueryBuilderTab(t.id); }}>×</span>
              </button>
            )}
          </For>

          {/* Schema Designer tab — top-level */}
          <For each={schemaDesignerTabs}>
            {(t) => (
              <button
                class="tab"
                classList={{ active: activeTabId() === t.id }}
                onClick={() => setActiveTabId(t.id)}
              >
                <span class="tab-home-icon">📐</span>
                <span class="tab-label">{t.title}</span>
                <span class="tab-close" onClick={(e) => { e.stopPropagation(); closeSchemaDesignerTab(t.id); }}>×</span>
              </button>
            )}
          </For>

          {/* Report Builder tab — top-level */}
          <For each={reportBuilderTabs}>
            {(t) => (
              <button
                class="tab"
                classList={{ active: activeTabId() === t.id }}
                onClick={() => setActiveTabId(t.id)}
              >
                <span class="tab-home-icon">📑</span>
                <span class="tab-label">{t.title}</span>
                <span class="tab-close" onClick={(e) => { e.stopPropagation(); closeReportBuilderTab(t.id); }}>×</span>
              </button>
            )}
          </For>

          {/* Server Variables & Status tab — top-level */}
          <For each={serverVarsTabs}>
            {(t) => (
              <button
                class="tab"
                classList={{ active: activeTabId() === t.id }}
                onClick={() => setActiveTabId(t.id)}
              >
                <span class="tab-home-icon">🔧</span>
                <span class="tab-label">{t.title}</span>
                <span class="tab-close" onClick={(e) => { e.stopPropagation(); closeServerVarsTab(t.id); }}>×</span>
              </button>
            )}
          </For>

          {/* Forms/Import DSL workbench tab — top-level */}
          <For each={formsDslTabs}>
            {(t) => (
              <button
                class="tab"
                classList={{ active: activeTabId() === t.id }}
                onClick={() => setActiveTabId(t.id)}
              >
                <span class="tab-home-icon">🧩</span>
                <span class="tab-label">{t.title}</span>
                <span class="tab-close" onClick={(e) => { e.stopPropagation(); closeFormsDslTab(t.id); }}>×</span>
              </button>
            )}
          </For>

          {/* Table Comparison tab — top-level */}
          <For each={tableCompareTabs}>
            {(t) => (
              <button
                class="tab"
                classList={{ active: activeTabId() === t.id }}
                onClick={() => setActiveTabId(t.id)}
              >
                <span class="tab-home-icon">⇄</span>
                <span class="tab-label">{t.title}</span>
                <span class="tab-close" onClick={(e) => { e.stopPropagation(); closeTableCompareTab(t.id); }}>×</span>
              </button>
            )}
          </For>

          {/* Reference Data Sync tab — top-level */}
          <For each={refSyncTabs}>
            {(t) => (
              <button
                class="tab"
                classList={{ active: activeTabId() === t.id }}
                onClick={() => setActiveTabId(t.id)}
              >
                <span class="tab-home-icon">🔗</span>
                <span class="tab-label">{t.title}</span>
                <span class="tab-close" onClick={(e) => { e.stopPropagation(); closeRefSyncTab(t.id); }}>×</span>
              </button>
            )}
          </For>

          {/* Data Anomaly Watcher tab — top-level */}
          <For each={anomalyTabs}>
            {(t) => (
              <button
                class="tab"
                classList={{ active: activeTabId() === t.id }}
                onClick={() => setActiveTabId(t.id)}
              >
                <span class="tab-home-icon">🚨</span>
                <span class="tab-label">{t.title}</span>
                <span class="tab-close" onClick={(e) => { e.stopPropagation(); closeAnomalyTab(t.id); }}>×</span>
              </button>
            )}
          </For>

          {/* A backup operator only gets the Backup/Restore tab; a read-only account
              is locked to Forms/Imports — neither gets dual-pane or fleet-query. */}
          <Show when={hasWorkspaces()}>
            <button class="tab tab-new-dual" title="New dual pane (stack two tabs side by side)"
                    onClick={createDualPane}>＋▥</button>
            <button class="tab tab-new-dual" title="New fleet query (route statements to connections via [connection] prefix)"
                    onClick={() => openFleetTab()}>＋⚡</button>
          </Show>
        </div>

        {/* ── Tab panels ── */}
        <div class="tab-panels">
          {/* Home panel — Connections page */}
          <Show when={showConnTab()}>
            <div class="tab-panel" classList={{ active: activeTabId() === 'home' }}>
              <Connections onOpen={openConnection} />
            </div>
          </Show>

          {/* On-demand system section panels (one per open section). The `lazy`
              sections mount only while active so they don't poll/fetch in the
              background; Users/Forms stay mounted while open (cheap). */}
          <For each={openSystemTabs()}>
            {(id) => (
              <div class="tab-panel" classList={{ active: activeTabId() === id }}>
                <Show when={id === 'users'}><Users /></Show>
                <Show when={id === 'forms'}><Forms /></Show>
                <Show when={id === 'audit' && activeTabId() === id}><Audit /></Show>
                <Show when={id === 'saved-imports' && activeTabId() === id}><SavedImports /></Show>
                <Show when={id === 'sessions' && activeTabId() === id}><Sessions /></Show>
                <Show when={id === 'backup-restore' && activeTabId() === id}><BackupRestorePanel /></Show>
                <Show when={id === 'result-history' && activeTabId() === id}><ResultHistoryPanel /></Show>
                <Show when={id === 'ai-actions' && activeTabId() === id}><AIActionsPanel active={activeTabId() === id} /></Show>
                <Show when={id === 'tools' && activeTabId() === id}><Tools onOpenFleetQuery={() => openFleetTab()} onOpenStructureSync={() => openStructureSyncTab()} onOpenScheduledJobs={() => openScheduledJobsTab()} onOpenFleetHealth={() => openFleetHealthTab()} onOpenAccessAudit={() => openAccessAuditTab()} onOpenQueryCompare={() => openQueryCompareTab()} onOpenTableCompare={() => openTableCompareTab()} onOpenRefSync={() => openRefSyncTab()} onOpenAnomalyWatcher={() => openAnomalyTab()} onOpenERDiagram={() => openERDiagramTab()} onOpenQueryBuilder={() => openQueryBuilderTab()} onOpenSchemaDesigner={() => openSchemaDesignerTab()} onOpenReportBuilder={() => openReportBuilderTab()} onOpenServerVars={() => openServerVarsTab()} onOpenFormsDsl={() => openFormsDslTab()} onOpenDependencies={() => openDependencyTab()} /></Show>
                <Show when={id === 'settings' && activeTabId() === id}><Settings /></Show>
              </div>
            )}
          </For>

          {/* One panel per open connection */}
          <For each={tabList()}>
            {(tab) => (
              <div class="tab-panel" classList={{ active: activeTabId() === tab.id }}>
                <Browser
                  connectionId={tab.connectionId}
                  connectionName={tab.connectionName}
                  state={tabState[tab.id]}
                  onFindDependencies={(initial) => openDependencyTab({ connectionId: tab.connectionId, ...initial })}
                  onSelectDB={(db) => selectDB(tab.id, db)}
                  onShowObjects={(db, category) => showObjects(tab.id, db, category)}
                  onCloseObjects={() => closeObjectsTab(tab.id)}
                  onOpenTable={(db, t, kind) => openTableTab(tab.id, db, t, kind)}
                  onCloseTable={(innerId) => closeTableTab(tab.id, innerId)}
                  onActivateInner={(innerId) => setActiveInner(tab.id, innerId)}
                  onSetDbPref={(db, partial) => setDbPref(tab.id, db, partial)}
                  onSetTableFilters={(innerId, f) => setTableFilters(tab.id, innerId, f)}
                  onOpenTableFiltered={(db, t, f) => openTableTabFiltered(tab.id, db, t, f)}
                  onOpenQuery={() => openQueryTab(tab.id)}
                  onOpenQueryWith={(db, sql, autoRun) => openQueryTabWith(tab.id, db, sql, autoRun)}
                  onCloseQuery={(innerId) => closeQueryTab(tab.id, innerId)}
                  onQueryDatabaseChange={(innerId, db) => updateQueryTabDb(tab.id, innerId, db)}
                  onQueryContentChange={(innerId, sql) => updateQueryTabContent(tab.id, innerId, sql)}
                  onQueryMessagesChange={(innerId, msgs) => updateQueryTabMessages(tab.id, innerId, msgs)}
                  onQuerySessionIdChange={(innerId, sid) => updateQueryTabSessionId(tab.id, innerId, sid)}
                  onQueryConnectionChange={(innerId, cid) => updateQueryTabConnection(tab.id, innerId, cid)}
                  onRenameQuery={(innerId, title) => renameQueryTab(tab.id, innerId, title)}
                  onOpenScript={(db, kind, name) => openScriptTab(tab.id, db, kind, name)}
                  onOpenNewScriptTab={(payload) => openNewScriptTab(tab.id, payload)}
                  onCloseScript={(innerId) => closeScriptTab(tab.id, innerId)}
                  onOpenTableEditTab={(db, table) => openTableEditTab(tab.id, db, table)}
                  onOpenNewTableEditTab={(db) => openNewTableEditTab(tab.id, db)}
                  onCloseTableEditTab={(innerId) => closeTableEditTab(tab.id, innerId)}
                  onTableDropped={(db, table) => closeTableTab(tab.id, `tbl-${db}-${table}`)}
                  onShowMySQLUsers={() => showMySQLUsers(tab.id)}
                  onCloseMySQLUsers={() => closeMySQLUsers(tab.id)}
                  onShowMonitor={() => showMonitor(tab.id)}
                  onCloseMonitor={() => closeMonitor(tab.id)}
                  assignmentFor={(innerId) => assignmentFor(tab.id, innerId)}
                  dualPanes={dualPanes}
                  onSendToDualPane={(innerId, kind, target, slot) => sendToDualPane(tab.id, innerId, kind, target, slot)}
                  onDetach={(innerId, kind) => detachToFloating(tab.id, innerId, kind)}
                />
              </div>
            )}
          </For>

          {/* One panel per dual-pane tab (kept mounted so slot portal targets persist) */}
          <For each={dualPanes}>
            {(pane) => (
              <div class="tab-panel" classList={{ active: activeTabId() === pane.id }}>
                <DualPane
                  pane={pane}
                  slotInfo={(slot) => slotInfo(pane.id, slot)}
                  registerSlotEl={(slot, el) => registerSlotEl(pane.id, slot, el)}
                  onRename={(t) => patchDualPane(pane.id, 'title', t)}
                  onOrientation={(o) => patchDualPane(pane.id, 'orientation', o)}
                  onRatio={(r) => patchDualPane(pane.id, 'ratio', r)}
                  onSyncScroll={(v) => patchDualPane(pane.id, 'syncScroll', v)}
                  onEjectSlot={(slot) => ejectSlot(pane.id, slot)}
                  onToggleResultsOnly={(slot) => toggleSlotResultsOnly(pane.id, slot)}
                />
              </div>
            )}
          </For>

          {/* One panel per fleet query tab */}
          <For each={fleetTabs}>
            {(t) => (
              <div class="tab-panel" classList={{ active: activeTabId() === t.id }}>
                <FleetQueryPanel
                  tab={t}
                  active={activeTabId() === t.id}
                  onSqlChange={(sql) => updateFleetTabSql(t.id, sql)}
                  onRename={(title) => renameFleetTab(t.id, title)}
                  onDefaultConnChange={(cid) => setFleetTabDefaultConn(t.id, cid)}
                  onModeChange={(mode) => setFleetTabMode(t.id, mode)}
                  onPresetsChange={(presets) => setFleetTabPresets(t.id, presets)}
                />
              </div>
            )}
          </For>

          {/* One panel per Structure Sync tab */}
          <For each={structureSyncTabs}>
            {(t) => (
              <div class="tab-panel" classList={{ active: activeTabId() === t.id }}>
                <StructureSyncPanel active={activeTabId() === t.id} />
              </div>
            )}
          </For>

          {/* One panel per Dependency Analyzer tab */}
          <For each={dependencyTabs}>
            {(t) => (
              <div class="tab-panel" classList={{ active: activeTabId() === t.id }}>
                <DependencyPanel initial={t.initial} onBack={() => closeDependencyTab(t.id)} onOpen={openObjectFloating} />
              </div>
            )}
          </For>

          {/* One panel per Scheduled Jobs tab */}
          <For each={scheduledJobsTabs}>
            {(t) => (
              <div class="tab-panel" classList={{ active: activeTabId() === t.id }}>
                <ScheduledJobsPanel active={activeTabId() === t.id} />
              </div>
            )}
          </For>

          {/* One panel per Fleet Health tab */}
          <For each={fleetHealthTabs}>
            {(t) => (
              <div class="tab-panel" classList={{ active: activeTabId() === t.id }}>
                <FleetHealthPanel active={activeTabId() === t.id} />
              </div>
            )}
          </For>

          {/* One panel per Access Audit tab */}
          <For each={accessAuditTabs}>
            {(t) => (
              <div class="tab-panel" classList={{ active: activeTabId() === t.id }}>
                <AccessAuditPanel active={activeTabId() === t.id} />
              </div>
            )}
          </For>

          {/* One panel per Query Comparison tab */}
          <For each={queryCompareTabs}>
            {(t) => (
              <div class="tab-panel" classList={{ active: activeTabId() === t.id }}>
                <QueryComparePanel active={activeTabId() === t.id} />
              </div>
            )}
          </For>

          {/* One panel per ER Diagram tab */}
          <For each={erDiagramTabs}>
            {(t) => (
              <div class="tab-panel" classList={{ active: activeTabId() === t.id }}>
                <ERDiagramPanel />
              </div>
            )}
          </For>

          {/* One panel per Visual Query Builder tab */}
          <For each={queryBuilderTabs}>
            {(t) => (
              <div class="tab-panel" classList={{ active: activeTabId() === t.id }}>
                <QueryBuilderPanel onOpenQuery={(conn, db, sql) => openQueryInConnection(conn, db, sql)} />
              </div>
            )}
          </For>

          {/* One panel per Schema Designer tab */}
          <For each={schemaDesignerTabs}>
            {(t) => (
              <div class="tab-panel" classList={{ active: activeTabId() === t.id }}>
                <SchemaDesignerPanel onOpenQuery={(conn, db, sql) => openQueryInConnection(conn, db, sql)} />
              </div>
            )}
          </For>

          {/* One panel per Report Builder tab */}
          <For each={reportBuilderTabs}>
            {(t) => (
              <div class="tab-panel" classList={{ active: activeTabId() === t.id }}>
                <ReportBuilderPanel />
              </div>
            )}
          </For>

          {/* One panel per Server Variables tab */}
          <For each={serverVarsTabs}>
            {(t) => (
              <div class="tab-panel" classList={{ active: activeTabId() === t.id }}>
                <ServerVariablesPanel />
              </div>
            )}
          </For>

          {/* One panel per Forms DSL workbench tab */}
          <For each={formsDslTabs}>
            {(t) => (
              <div class="tab-panel" classList={{ active: activeTabId() === t.id }}>
                <FormsDslPanel />
              </div>
            )}
          </For>

          {/* One panel per Table Comparison tab */}
          <For each={tableCompareTabs}>
            {(t) => (
              <div class="tab-panel" classList={{ active: activeTabId() === t.id }}>
                <TableComparePanel active={activeTabId() === t.id} />
              </div>
            )}
          </For>

          {/* One panel per Reference Data Sync tab */}
          <For each={refSyncTabs}>
            {(t) => (
              <div class="tab-panel" classList={{ active: activeTabId() === t.id }}>
                <RefSyncPanel active={activeTabId() === t.id} />
              </div>
            )}
          </For>

          {/* One panel per Data Anomaly Watcher tab */}
          <For each={anomalyTabs}>
            {(t) => (
              <div class="tab-panel" classList={{ active: activeTabId() === t.id }}>
                <AnomalyWatcherPanel active={activeTabId() === t.id} />
              </div>
            )}
          </For>
        </div>

        <Toasts />
        <LockOverlay />

        {/* Sign-out dialog — scope chooser, with a second confirm for the
            admin-only "sign out everyone". */}
        <Show when={signoutStep() === 'menu'}>
          <Modal open={true} title="Sign out" onClose={() => setSignoutStep(null)}>
            <p class="muted" style="font-size:13px;margin:0 0 14px;">
              Signed in as <strong>{currentUser()?.username}</strong>. Choose what to sign out.
            </p>
            <div class="signout-options">
              <button class="btn" disabled={signingOut()} onClick={() => doLogout('device')}>
                Sign out — this device
              </button>
              <button class="btn btn-ghost" disabled={signingOut()} onClick={() => doLogout('mine')}>
                Sign out — all my devices
              </button>
              <Show when={isAdmin()}>
                <div class="signout-sep" />
                <button class="btn btn-danger" disabled={signingOut()} onClick={() => setSignoutStep('confirm-all')}
                        title="Force every logged-in user on this server to sign out">
                  Sign out everyone (all users)…
                </button>
              </Show>
            </div>
            <div class="modal-actions" style="margin-top:14px;">
              <button class="btn btn-ghost" disabled={signingOut()} onClick={() => setSignoutStep(null)}>Cancel</button>
            </div>
          </Modal>
        </Show>

        <Show when={signoutStep() === 'confirm-all'}>
          <Modal open={true} title="Sign out everyone?" onClose={() => setSignoutStep('menu')}>
            <p style="font-size:13px;margin:0 0 6px;color:var(--danger,#ef4444);font-weight:600;">
              ⚠ This signs out EVERY user on this server immediately.
            </p>
            <p class="muted" style="font-size:12px;margin:0 0 16px;">
              All active sessions (including other admins, and you) are invalidated — everyone has to
              log in again. Open query tabs and transactions across the fleet are dropped. This is audit-logged.
            </p>
            <div class="modal-actions">
              <button class="btn btn-ghost" disabled={signingOut()} onClick={() => setSignoutStep('menu')}>Back</button>
              <button class="btn btn-danger" disabled={signingOut()} onClick={() => doLogout('everyone')}>
                {signingOut() ? 'Signing out…' : 'Yes, sign out all users'}
              </button>
            </div>
          </Modal>
        </Show>

        {/* Search Everywhere (Ctrl+P) — quick-open any object on the active connection. */}
        <QuickOpenModal
          open={!!quickOpen()}
          connectionId={quickOpen()?.connectionId}
          currentDatabase={quickOpen()?.database}
          onOpen={onQuickOpen}
          onClose={() => setQuickOpen(null)}
        />

        {/* Command palette (Ctrl+K) — action launcher. */}
        <CommandPalette open={cmdOpen()} commands={cmdOpen() ? buildCommands() : []} onClose={() => setCmdOpen(false)} />
        <ObjectViewer open={objViewerOpen()} onClose={() => setObjViewerOpen(false)} />

        {/* Detached inner tabs — each hosts a relocated, still-live inner tab. They
            float over every top tab (the relocated DOM lives here, not under the
            connection's hidden tab-panel), so a detached query stays visible while
            you browse elsewhere. Closing (✕) or Reattach returns it to the strip. */}
        <For each={floatingPanels}>
          {(fp) => (
            <FloatingPanel
              open={true}
              resizable
              title={innerTabTitle(fp.connTabId, fp.innerId, fp.kind)}
              width={fp.w}
              height={fp.h}
              initialPos={{ x: fp.x, y: fp.y }}
              closeOnEsc={false}
              onGeometry={(g) => patchFloatGeometry(fp.id, g)}
              onClose={() => reattachFloating(fp.id)}
              headerActions={
                <button class="fp-act" title="Reattach to the tab strip" onClick={() => reattachFloating(fp.id)}>⇲ Reattach</button>
              }
            >
              <div class="fp-detach-body" ref={(el) => registerFloatEl(fp.id, el)} />
            </FloatingPanel>
          )}
        </For>

        <VersionHistory open={versionOpen()} onClose={() => setVersionOpen(false)} />
      </div>
    </Show>
  );
}
